import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useAuth } from '../../../context/AuthContext';

interface FamilySelectionStepProps {
  selections: any;
  onUpdateSelections: (update: any) => void;
  limit: number;
  recommendations?: string[];
}

interface Family {
  id: string;
  name: string;
  description?: string;
  createdAt: any;
  memberCount?: number;
}

export default function FamilySelectionStep({
  selections,
  onUpdateSelections,
  limit,
  recommendations = [],
}: FamilySelectionStepProps) {
  const { user } = useAuth();
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // IDs seleccionados
  const selected = selections.activeFamilyIds || [];
  
  useEffect(() => {
    loadFamilies();
  }, [user?.uid]);
  
  const loadFamilies = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      // Obtener familias donde el usuario es miembro
      const familiesRef = collection(db, 'families');
      const q = query(familiesRef, where('members', 'array-contains', user.uid));
      const snapshot = await getDocs(q);
      
      const loadedFamilies: Family[] = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'Sin nombre',
        description: doc.data().description || '',
        createdAt: doc.data().createdAt,
        memberCount: doc.data().members?.length || 0,
      }));
      
      setFamilies(loadedFamilies);
      
      // Pre-seleccionar recomendadas si existen
      if (recommendations.length > 0 && selected.length === 0) {
        const toSelect = recommendations.slice(0, limit);
        onUpdateSelections({ activeFamilyIds: toSelect });
      }
    } catch (error) {
      console.error('Error cargando familias:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleSelection = (id: string) => {
    let newSelected = [...selected];
    
    if (newSelected.includes(id)) {
      newSelected = newSelected.filter(x => x !== id);
    } else {
      if (newSelected.length < limit) {
        newSelected.push(id);
      }
    }
    
    onUpdateSelections({ activeFamilyIds: newSelected });
  };
  
  const selectRecommended = () => {
    const toSelect = recommendations.slice(0, limit);
    onUpdateSelections({ activeFamilyIds: toSelect });
  };
  
  // Filtrar por búsqueda
  const filteredFamilies = families.filter(f =>
    f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Ordenar: recomendadas primero, luego por nombre
  const sortedFamilies = [...filteredFamilies].sort((a, b) => {
    const aRecommended = recommendations.includes(a.id);
    const bRecommended = recommendations.includes(b.id);
    if (aRecommended && !bRecommended) return -1;
    if (!aRecommended && bRecommended) return 1;
    return a.name.localeCompare(b.name);
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando grupos familiares...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Selecciona {limit === 1 ? 'tu Grupo' : `hasta ${limit} Grupos`}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {limit === 1 
            ? 'Solo puedes mantener un grupo activo en el Plan Gratuito'
            : `El Plan Gratuito te permite mantener hasta ${limit} grupos activos`
          }
        </p>
      </div>
      
      {/* Contador de selección */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <span className="text-2xl">
            {selected.length >= limit ? '✅' : '📊'}
          </span>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {selected.length} de {limit} seleccionado{limit > 1 ? 's' : ''}
          </span>
        </div>
      </div>
      
      {/* Búsqueda */}
      <div>
        <input
          type="text"
          placeholder="🔍 Buscar grupo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>
      
      {/* Botón de recomendadas */}
      {recommendations.length > 0 && (
        <button
          onClick={selectRecommended}
          className="w-full px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
        >
          ✨ Seleccionar Recomendado{limit > 1 ? 's' : ''}
        </button>
      )}
      
      {/* Lista de familias */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {sortedFamilies.map((family) => {
          const isSelected = selected.includes(family.id);
          const isRecommended = recommendations.includes(family.id);
          const canSelect = selected.length < limit || isSelected;
          
          return (
            <div
              key={family.id}
              onClick={() => canSelect && toggleSelection(family.id)}
              className={`
                p-4 rounded-lg border-2 cursor-pointer transition-all
                ${isSelected 
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
                }
                ${!canSelect && 'opacity-50 cursor-not-allowed'}
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">
                      {isSelected ? '✅' : '👨‍👩‍👧‍👦'}
                    </span>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {family.name}
                    </h3>
                    {isRecommended && (
                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                        🔸 RECOMENDADO
                      </span>
                    )}
                  </div>
                  {family.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {family.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>👤 {family.memberCount || 0} miembro{family.memberCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="ml-4">
                  <div className={`
                    w-6 h-6 rounded border-2 flex items-center justify-center
                    ${isSelected 
                      ? 'bg-green-500 border-green-500' 
                      : 'border-gray-300 dark:border-gray-600'
                    }
                  `}>
                    {isSelected && <span className="text-white text-sm">✓</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        {sortedFamilies.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm 
              ? '🔍 No se encontraron grupos con ese nombre'
              : '📭 No tienes grupos creados'
            }
          </div>
        )}
      </div>
      
      {/* Mensaje informativo */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          💡 <strong>Los grupos no seleccionados se pausarán temporalmente</strong>. 
          No se eliminará ningún dato y podrás recuperarlos al renovar tu plan.
        </p>
      </div>
    </div>
  );
}
